import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiLink, FiCalendar } from 'react-icons/fi';

interface ChangeLinkedEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: any;
  searchEventQuery: string;
  setSearchEventQuery: (val: string) => void;
  filteredEvents: any[];
  selectedEventUuid: string;
  setSelectedEventUuid: (val: string) => void;
  onSave: (eventUuid: string) => void;
  onDetach: () => void;
}

export const ChangeLinkedEventModal: React.FC<ChangeLinkedEventModalProps> = ({
  isOpen,
  onClose,
  project,
  searchEventQuery,
  setSearchEventQuery,
  filteredEvents,
  selectedEventUuid,
  setSelectedEventUuid,
  onSave,
  onDetach,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-lg bg-[#0B0B0B] border border-white/10 rounded-[28px] p-6 text-left flex flex-col max-h-[85vh] z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className="text-xl font-bold text-white">Change Linked Event</h2>
                <p className="text-zinc-550 text-xs mt-1">
                  Connect this project to an event on Musicade.
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            {/* Currently Linked Event Card */}
            <div className="mb-5">
              {project.linkedEvent ? (
                <div className="bg-[#0C2540]/60 border border-blue-500/20 rounded-2xl p-4 flex items-center gap-3.5">
                  <div className="w-9 h-9 rounded-full bg-blue-500/10 text-blue-400 flex items-center justify-center shrink-0">
                    <FiLink className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-blue-300 text-xs font-semibold">Currently linked</p>
                    <p className="text-white font-bold text-base mt-0.5">{project.linkedEvent}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-zinc-900/60 border border-white/5 rounded-2xl p-4 flex items-center gap-3.5">
                  <div className="w-9 h-9 rounded-full bg-white/[0.04] text-zinc-500 flex items-center justify-center shrink-0">
                    <FiLink className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs font-semibold">Currently linked</p>
                    <p className="text-zinc-400 font-bold text-base mt-0.5">No event linked</p>
                  </div>
                </div>
              )}
            </div>

            {/* Search input */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search your events..."
                value={searchEventQuery}
                onChange={(e) => setSearchEventQuery(e.target.value)}
                className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3 transition-all"
              />
            </div>

            {/* Events scroll area */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-1 max-h-[300px] scrollbar-thin">
              {filteredEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center text-zinc-500">
                  <FiCalendar className="w-8 h-8 mb-2 text-zinc-700" />
                  <p className="text-xs font-semibold">No events found</p>
                </div>
              ) : (
                filteredEvents.map((evt) => {
                  const isSelected = selectedEventUuid === evt.id;
                  return (
                    <button
                      key={evt.id}
                      type="button"
                      onClick={() => setSelectedEventUuid(evt.id)}
                      className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all text-left cursor-pointer ${
                        isSelected
                          ? 'border-blue-500 bg-blue-500/5'
                          : 'border-white/5 bg-[#0F0F0F]/60 hover:border-white/10 hover:bg-white/[0.02]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <FiCalendar
                          className={`w-4 h-4 ${isSelected ? 'text-blue-400' : 'text-zinc-500'}`}
                        />
                        <div>
                          <p className="text-white font-bold text-sm">{evt.title}</p>
                          <p className="text-zinc-500 text-xs mt-0.5">
                            {evt.date} • {evt.type}
                          </p>
                        </div>
                      </div>

                      {/* Custom radio button */}
                      <div
                        className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${
                          isSelected ? 'border-blue-500 bg-blue-500' : 'border-zinc-700'
                        }`}
                      >
                        {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/5 shrink-0">
              {/* Detach button */}
              <button
                type="button"
                onClick={onDetach}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-rose-500/20 bg-rose-500/10 hover:bg-rose-500/20 text-rose-450 hover:text-rose-350 text-xs font-bold transition-all cursor-pointer"
              >
                <svg
                  className="w-3.5 h-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  <line x1="3" y1="3" x2="21" y2="21" />
                </svg>
                <span>Detach</span>
              </button>

              {/* Right group */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2.5 rounded-full border border-white/10 hover:bg-white/5 text-zinc-400 hover:text-white text-xs font-semibold transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => onSave(selectedEventUuid)}
                  className="px-6 py-2.5 rounded-full bg-white text-black hover:bg-zinc-100 text-xs font-bold transition-all cursor-pointer shadow-lg shadow-white/5 active:scale-95"
                >
                  Save
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
